import { createRequestHeaders } from '@zg-rentals/particles-js-requester';
import { logError } from '@zg-rentals/log-error';

import { formatError } from './errorUtils';

import { AppSelectors, EnvSelectors, UserSelectors } from '../selectors';

import DevToolActions from '../actions/DevToolActions';
import { updateUserCookies } from '../slices/user';

import { API_LIST_ITEM_SOURCE } from '../reducers/devTool';

import ErrorRMW from '../model/ErrorRMW';

import type { Method } from 'axios';
import type { Dispatch } from 'react';
import type { client } from './requesters';
import type { RestMethodNameProps } from '../constants/StringConstants';

const requestWrapper =
  (request: typeof client, methodName: Method, endpoint: string, config: { data?: unknown } = {}) =>
  async (dispatch: Dispatch<unknown>, getState: () => unknown) => {
    const state = getState();
    const headers = __BROWSER__
      ? {
          headers: {
            'x-zone-id': AppSelectors.makeTimeZone(state),
            'x-brand': 'zillow',
          },
        }
      : {};
    const requestConfig = {
      ...config,
      headers: createRequestHeaders({
        brand: AppSelectors.makeBrand(state) || 'zillow',
        ip: AppSelectors.makeIp(state),
        userAgent: AppSelectors.makeUserAgent(state),
        isNode: __SERVER__,
        buildId: String(__BUILD_NUMBER__),
        method: methodName,
        cookies: UserSelectors.makeOriginalServerSideRequestCookies(state),
        ...headers,
      }),
    };

    // Allow axios to auto-detect Content-Type for FormData (multipart/form-data with boundary)
    if (config.data instanceof FormData) {
      delete requestConfig.headers['Content-Type'];
    }

    try {
      const response = await request(EnvSelectors.makeZgApi(state))[methodName as RestMethodNameProps](
        endpoint,
        config.data ? config.data : requestConfig,
        config.data ? requestConfig : undefined,
      );

      dispatch(
        DevToolActions.addDevToolApiList({
          endpoint,
          source: __BROWSER__ ? API_LIST_ITEM_SOURCE.CLIENT : API_LIST_ITEM_SOURCE.MIDDLEWARE,
        }),
      );

      // update server side request cookies based on api response
      // used by renderer.js to return the correct cookies in header of o.g. request
      if (response.headers['set-cookie']) {
        dispatch(
          updateUserCookies({
            apiResponseServerSideRequestCookies: response.headers['set-cookie'],
          }),
        );
      }

      return response.data;
    } catch (err) {
      const data = err && err.response ? err.response.data : undefined;

      /**
       * NOTE fix for 10,000 byte limit for logger needs to happen after `data` is referenced
       * related issue: https://zillowgroup.atlassian.net/browse/ZRM-13946
       */
      err.config = '';
      err.request = '';
      err.response = '';

      logError({
        error: err,
        errorType: 'HTTPError',
        context: {
          endpoint,
          method: methodName,
        },
      });

      const { errorCopy, rawErrorsCopy } = formatError(data);

      // normalize error
      const normalizedErrorCopy = ErrorRMW(errorCopy);

      return {
        success: false,
        error: normalizedErrorCopy,
        errors: rawErrorsCopy,
        response: data ? data.response : {},
      };
    }
  };

export default requestWrapper;
